import { ShieldAlert, Users } from "lucide-react";

import { cn } from "../../lib/utils";
import { Input } from "../ui";
import type { QueueSettingsForm } from "./useQueueSettingsForm";

type Props = {
  form: QueueSettingsForm;
};

function RestrictionCard({
  icon: Icon,
  title,
  description,
  disabled,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-surface-700/40 overflow-hidden transition-all duration-200",
        disabled && "opacity-50",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-3 px-4 py-3",
          disabled ? "bg-surface-800/30" : "bg-surface-800/60",
        )}
      >
        <div className="flex items-center justify-center size-8 rounded-lg shrink-0 bg-surface-700/50 text-surface-400">
          <Icon className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-white">{title}</h4>
          <p className="text-2xs text-surface-500 leading-snug">{description}</p>
        </div>
        {disabled && (
          <span className="text-2xs font-bold text-surface-500 bg-surface-700/40 px-2 py-0.5 rounded uppercase tracking-wider shrink-0">
            Coming soon
          </span>
        )}
      </div>
      <div className="px-4 pb-4 pt-3 border-t border-surface-700/30">{children}</div>
    </div>
  );
}

export function RestrictionsSection({ form }: Props) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-surface-500">
        Limit who can join this queue based on rating and party composition.
      </p>

      {/* ELO Restrictions */}
      <RestrictionCard
        icon={ShieldAlert}
        title="ELO Restrictions"
        description="Set min/max rating requirements to join this queue"
        disabled
      >
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Min ELO"
            type="number"
            value=""
            onChange={() => {}}
            placeholder="None"
            suffix="ELO"
            disabled
          />
          <Input
            label="Max ELO"
            type="number"
            value=""
            onChange={() => {}}
            placeholder="None"
            suffix="ELO"
            disabled
          />
        </div>
      </RestrictionCard>

      {/* Party Restrictions */}
      <RestrictionCard
        icon={Users}
        title="Party Restrictions"
        description="Control group sizes and rating spread within parties"
      >
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Max Party Size"
            type="number"
            value={form.maxPartySize}
            onChange={(e) => form.setMaxPartySize(e.target.value)}
            placeholder="Team size"
            min={1}
          />
          <Input
            label="Party ELO Disparity"
            type="number"
            value={form.partyEloDisparity}
            onChange={(e) => form.setPartyEloDisparity(e.target.value)}
            placeholder="Unlimited"
            suffix="ELO"
            min={0}
          />
        </div>
      </RestrictionCard>
    </div>
  );
}
